import { useMemo, useState } from 'react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import { apiClient } from '../services/api'
import { useTranslation } from '../contexts/TranslationContext'
import { formatEmptyValue } from '../utils/displayValue'

function parseInputItems(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((description, index) => ({ id: `line-${index + 1}`, description }))
}

function toPercent(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return formatEmptyValue(null)
  return `${Math.round(number * 100)}%`
}

export default function HybridCategorization() {
  const { t } = useTranslation()
  const [inputText, setInputText] = useState('')
  const [useLlmFallback, setUseLlmFallback] = useState(true)
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const parsedItems = useMemo(() => parseInputItems(inputText), [inputText])

  const handleClassify = async () => {
    if (parsedItems.length === 0) {
      setError(t('Please enter at least one product line.', 'Please enter at least one product line.'))
      setResults([])
      return
    }

    try {
      setLoading(true)
      setError('')
      const response = await apiClient.post('/categories/classify', {
        category_type: 'product',
        use_llm_fallback: useLlmFallback,
        items: parsedItems,
      })
      setResults(Array.isArray(response.data?.data) ? response.data.data : [])
    } catch (err) {
      console.error('Hybrid categorization failed', err)
      setError(t('Failed to classify products.', 'Failed to classify products.'))
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('Hybrid Categorization', 'Hybrid Categorization')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('Classify products with deterministic rules and optional LLM fallback.', 'Classify products with deterministic rules and optional LLM fallback.')}
        </p>
      </div>

      <Card>
        <div className="space-y-4">
          <label htmlFor="hybrid-categorization-items" className="block text-sm font-medium text-foreground">
            {t('Products (one per line)', 'Products (one per line)')}
          </label>
          <textarea
            id="hybrid-categorization-items"
            aria-label={t('Products (one per line)', 'Products (one per line)')}
            value={inputText}
            onChange={(event) => setInputText(event.target.value)}
            className="w-full min-h-44 rounded-md border border-outline px-3 py-2 text-sm text-foreground focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-300"
            placeholder={t('Example: Milk\nBread\nApple Juice', 'Example: Milk\nBread\nApple Juice')}
          />

          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={useLlmFallback}
              onChange={(event) => setUseLlmFallback(event.target.checked)}
              className="h-4 w-4 rounded border-outline text-blue-600 focus:ring-blue-500"
            />
            <span>{t('Use LLM fallback for unresolved items', 'Use LLM fallback for unresolved items')}</span>
          </label>

          <div className="flex items-center gap-3">
            <Button onClick={handleClassify} disabled={loading}>
              {loading ? t('Classifying...', 'Classifying...') : t('Classify', 'Classify')}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t('Items', 'Items')}: {parsedItems.length}
            </span>
          </div>

          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-outline px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">{t('Results', 'Results')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-surface-elevated text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">{t('Product', 'Product')}</th>
                <th className="px-4 py-3 text-left">{t('Category', 'Category')}</th>
                <th className="px-4 py-3 text-left">{t('Source', 'Source')}</th>
                <th className="px-4 py-3 text-right">{t('Confidence', 'Confidence')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-surface">
              {results.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    {t('No results yet.', 'No results yet.')}
                  </td>
                </tr>
              ) : results.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-foreground">{formatEmptyValue(item.description)}</td>
                  <td className="px-4 py-3 text-foreground">{formatEmptyValue(item.category?.name)}</td>
                  <td className="px-4 py-3 text-foreground">{formatEmptyValue(item.source)}</td>
                  <td className="px-4 py-3 text-right text-foreground">{toPercent(item.confidence)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

